import React, { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { DateRange } from "react-day-picker";
import {
  ChevronRight,
  History,
  Landmark,
  LucideIcon,
  Paperclip,
  Smartphone,
  UserRound,
} from "lucide-react";
import CustomTabs from "../components/ui/tabs";
import DataTable, { TableRowData } from "../components/data-table";
import SearchInput from "../components/search-input";
import SelectInput from "../components/select-input";
import DateRangePicker from "../components/date-range-picker";
import PageLoadingState from "../components/page-loading-state";
import PageShell from "../components/page-shell";
import FilterField from "../components/mobile/filter-field";
import MobileDateRangeFields from "../components/mobile/mobile-date-range-fields";
import { RefreshToolbarButton } from "../components/refresh-toolbar-button";
import ActivityLogDetailsDrawer from "../components/activity-log-details-drawer";
import BankDetailsSection from "../components/settings-bank-details-section";
import MobileAppSettingsSection from "../components/settings-mobile-app-section";
import AttachmentTypesSection from "../components/settings-attachment-types-section";
import { Badge } from "../components/ui/badge";
import { Icons } from "../components/ui/icons";
import { useUser } from "../hooks/use-user";
import { useIsMobile } from "../hooks/use-is-mobile";
import { UserRole } from "../lib/enums";
import {
  ActivityLog,
  getActivityLogs,
  getUsers,
} from "../lib/api";
import { cn, getBackendErrorMessage, getBadgeVariant } from "../lib/utils";
import { toast } from "sonner";

const ACTIVITY_LOG_COLUMNS = [
  { header: "Date", accessor: "date", editable: false },
  { header: "User", accessor: "user", editable: false },
  { header: "Action", accessor: "action", editable: false },
  { header: "Entity Type", accessor: "entityType", editable: false },
  { header: "Entity ID", accessor: "entityId", editable: false },
  { header: "Details", accessor: "details", editable: false },
];

const ACTION_FILTER_OPTIONS = [
  { label: "All actions", value: "all" },
  { label: "CREATE", value: "CREATE" },
  { label: "UPDATE", value: "UPDATE" },
  { label: "DELETE", value: "DELETE" },
  { label: "LOGIN", value: "LOGIN" },
  { label: "CANCEL", value: "CANCEL" },
  { label: "EXTEND_DAYS_HIRED", value: "EXTEND_DAYS_HIRED" },
  { label: "ADD_DOCUMENT", value: "ADD_DOCUMENT" },
  { label: "REMOVE_DOCUMENT", value: "REMOVE_DOCUMENT" },
  { label: "ADD_ADDITION", value: "ADD_ADDITION" },
  { label: "PROVIDE_PPE", value: "PROVIDE_PPE" },
];

const ENTITY_TYPE_FILTER_OPTIONS = [
  { label: "All types", value: "all" },
  { label: "USER", value: "USER" },
  { label: "CUSTOMER", value: "CUSTOMER" },
  { label: "VENDOR", value: "VENDOR" },
  { label: "SITE", value: "SITE" },
  { label: "WAREHOUSE", value: "WAREHOUSE" },
  { label: "INVENTORY", value: "INVENTORY" },
  { label: "QUOTATION", value: "QUOTATION" },
  { label: "INVOICE", value: "INVOICE" },
  { label: "ITEM MOVEMENT", value: "ITEM_MOVEMENT" },
  { label: "BANK DETAIL", value: "BANK_DETAIL" },
  { label: "FIXED ASSET", value: "FIXED_ASSET" },
  { label: "PPE", value: "PPE" },
  { label: "PPE USER", value: "PPE_USER" },
  { label: "PPE PROVISION", value: "PPE_PROVISION" },
];

function formatActivityLogDetails(log: ActivityLog): string {
  if (!log.details) return "—";
  const changes = log.details.changes;
  if (changes && typeof changes === "object") {
    const s = JSON.stringify(changes);
    return s.length > 80 ? `${s.slice(0, 77)}...` : s;
  }
  const s = JSON.stringify(log.details);
  return s.length > 80 ? `${s.slice(0, 77)}...` : s;
}

function getInitials(fullName: string): string {
  return fullName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

interface ActivityLogsSectionProps {
  onSelectLog: (log: ActivityLog | null) => void;
  mobileBackTo?: string | number;
}

const ActivityLogsSection: React.FC<ActivityLogsSectionProps> = ({
  onSelectLog,
  mobileBackTo,
}) => {
  const isMobile = useIsMobile();
  const { user } = useUser();
  const canFilterByUser =
    user?.role === UserRole.ADMIN || user?.role === UserRole.MANAGEMENT;

  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [actionFilter, setActionFilter] = useState("all");
  const [entityTypeFilter, setEntityTypeFilter] = useState("all");
  const [userFilter, setUserFilter] = useState("all");
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const queryClient = useQueryClient();

  const dateFromParam = dateRange?.from ? format(dateRange.from, "yyyy-MM-dd") : undefined;
  const dateToParam = dateRange?.to ? format(dateRange.to, "yyyy-MM-dd") : undefined;
  const actionParam = actionFilter !== "all" ? actionFilter : undefined;
  const entityTypeParam = entityTypeFilter !== "all" ? entityTypeFilter : undefined;
  const userIdParam =
    userFilter !== "all" ? userFilter : undefined;

  const usersQuery = useQuery({
    queryKey: ["users", { page: 1, limit: 500, search: "" }],
    queryFn: async () => getUsers(1, 500, ""),
    enabled: canFilterByUser,
  });

  const activityLogsQuery = useQuery({
    queryKey: [
      "activity-logs",
      {
        page: currentPage,
        limit: pageSize,
        search: searchTerm,
        userId: userIdParam,
        action: actionParam,
        entityType: entityTypeParam,
        dateFrom: dateFromParam,
        dateTo: dateToParam,
      },
    ],
    queryFn: async () =>
      getActivityLogs({
        page: currentPage,
        limit: pageSize,
        search: searchTerm || undefined,
        userId: userIdParam,
        action: actionParam,
        entityType: entityTypeParam,
        dateFrom: dateFromParam,
        dateTo: dateToParam,
      }),
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (!activityLogsQuery.error) return;
    toast.error(getBackendErrorMessage(activityLogsQuery.error, "Failed to fetch activity logs"));
  }, [activityLogsQuery.error]);

  useEffect(() => {
    const handler = setTimeout(() => setSearchTerm(searchInput), 400);
    return () => clearTimeout(handler);
  }, [searchInput]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, actionFilter, entityTypeFilter, userFilter, dateRange]);

  const userFilterOptions = useMemo(() => {
    const opts = [{ label: "All users", value: "all" }];
    const users = usersQuery.data?.data ?? [];
    return [
      ...opts,
      ...users.map((u) => ({ label: u.fullName, value: u.id })),
    ];
  }, [usersQuery.data]);

  const tableData: TableRowData[] = useMemo(() => {
    return (activityLogsQuery.data?.data ?? []).map((log) => ({
      id: log.id,
      date: format(new Date(log.createdAt), "MMM dd, yyyy 'at' hh:mm a"),
      user: log.user?.fullName ?? "—",
      action: log.action,
      entityType: log.entityType,
      entityId: log.entityId ?? "—",
      details: formatActivityLogDetails(log),
      raw_data: log,
    }));
  }, [activityLogsQuery.data]);

  const totalItems = activityLogsQuery.data?.meta.total ?? 0;
  const totalPages = activityLogsQuery.data?.meta.totalPages ?? 1;
  const initialLoading = activityLogsQuery.isLoading;
  const tableLoading = activityLogsQuery.isFetching && !activityLogsQuery.isLoading;

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["activity-logs"] });

  const logActions = [
    {
      label: "View details",
      icon: Icons.Preview,
      onClick: (row: TableRowData) => {
        onSelectLog((row as { raw_data?: ActivityLog }).raw_data ?? null);
      },
    },
  ];

  const logCount = (
    <p className="text-sm text-gray-500">
      {totalItems.toLocaleString()} log{totalItems === 1 ? "" : "s"} matching your filters
    </p>
  );

  const logsTable = (
    <DataTable
      columns={ACTIVITY_LOG_COLUMNS}
      data={tableData}
      actions={logActions}
      loading={tableLoading}
      pagination={{
        page: currentPage,
        pageSize,
        totalItems,
        totalPages,
        onPageChange: setCurrentPage,
        onPageSizeChange: setPageSize,
      }}
    />
  );

  if (isMobile) {
    const activeFilterCount = [
      actionFilter !== "all",
      entityTypeFilter !== "all",
      canFilterByUser && userFilter !== "all",
      !!dateRange?.from,
    ].filter(Boolean).length;

    return (
      <PageShell
        title="Activity logs"
        loading={initialLoading}
        mobile={{
          backTo: mobileBackTo,
          onRefresh: refresh,
          activeFilterCount,
          onResetFilters: () => {
            setActionFilter("all");
            setEntityTypeFilter("all");
            setUserFilter("all");
            setDateRange(undefined);
          },
          filters: (
            <>
              <FilterField label="Action">
                <SelectInput
                  value={actionFilter}
                  options={ACTION_FILTER_OPTIONS}
                  placeholder="Action"
                  onChange={setActionFilter}
                />
              </FilterField>
              <FilterField label="Entity type">
                <SelectInput
                  value={entityTypeFilter}
                  options={ENTITY_TYPE_FILTER_OPTIONS}
                  placeholder="Entity type"
                  onChange={setEntityTypeFilter}
                />
              </FilterField>
              {canFilterByUser && (
                <FilterField label="User">
                  <SelectInput
                    value={userFilter}
                    options={userFilterOptions}
                    placeholder="User"
                    onChange={setUserFilter}
                  />
                </FilterField>
              )}
              <FilterField label="Date range">
                <MobileDateRangeFields value={dateRange} onChange={setDateRange} />
              </FilterField>
            </>
          ),
        }}
      >
        <SearchInput
          searchTerm={searchInput}
          onSearchChange={setSearchInput}
          placeholder="Search logs..."
        />
        {logCount}
        {logsTable}
      </PageShell>
    );
  }

  if (initialLoading) {
    return <PageLoadingState showAnalytics={false} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <SearchInput
          searchTerm={searchInput}
          onSearchChange={setSearchInput}
          placeholder="Search logs..."
        />
        <SelectInput
          value={actionFilter}
          options={ACTION_FILTER_OPTIONS}
          placeholder="Action"
          onChange={setActionFilter}
        />
        <SelectInput
          value={entityTypeFilter}
          options={ENTITY_TYPE_FILTER_OPTIONS}
          placeholder="Entity type"
          onChange={setEntityTypeFilter}
        />
        {canFilterByUser && (
          <SelectInput
            value={userFilter}
            options={userFilterOptions}
            placeholder="User"
            onChange={setUserFilter}
          />
        )}
        <DateRangePicker value={dateRange} onChange={setDateRange} className="shrink-0" />
        <RefreshToolbarButton
          className="ml-auto"
          isRefreshing={activityLogsQuery.isFetching}
          onRefresh={refresh}
        />
      </div>

      {logCount}

      {logsTable}
    </div>
  );
};

const AccountSection: React.FC = () => {
  const { user } = useUser();

  if (!user) {
    return <p className="text-sm text-gray-500">No user information available.</p>;
  }

  const fields = [
    { label: "Full name", value: user.fullName },
    { label: "Email", value: user.email },
    { label: "Phone", value: user.phoneNumber || "—" },
    { label: "Role", value: user.role },
    { label: "Status", value: user.status },
    {
      label: "Location",
      value: [user.ward, user.district, user.region, user.country].filter(Boolean).join(", ") || "—",
    },
  ];

  return (
    <div className="border border-gray-200 rounded-xl p-6 max-w-lg bg-white">
      <div className="flex items-center gap-4 mb-6">
        <div className="w-12 h-12 rounded-lg bg-black text-white flex items-center justify-center text-lg font-medium">
          {getInitials(user.fullName)}
        </div>
        <div>
          <h3 className="font-semibold text-gray-900">{user.fullName}</h3>
          <p className="text-sm text-gray-500">{user.email}</p>
        </div>
        <Badge variant={getBadgeVariant(user.status)} className="ml-auto">
          {user.status}
        </Badge>
      </div>
      <dl className="space-y-3">
        {fields.map((f) => (
          <div key={f.label} className="flex flex-col sm:flex-row sm:gap-4">
            <dt className="text-sm font-medium text-gray-500 sm:w-28 shrink-0">{f.label}</dt>
            <dd className="text-sm text-gray-900">{f.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

interface SettingsTab {
  id: string;
  label: string;
  content: React.ReactNode;
}

const MOBILE_SECTION_META: Record<
  string,
  { icon: LucideIcon; iconClassName: string; description: string }
> = {
  account: {
    icon: UserRound,
    iconClassName: "bg-gray-900",
    description: "Your profile and role",
  },
  "bank-details": {
    icon: Landmark,
    iconClassName: "bg-emerald-500",
    description: "Payment instructions on documents",
  },
  attachments: {
    icon: Paperclip,
    iconClassName: "bg-orange-500",
    description: "Upload types for orders and requests",
  },
  "mobile-app": {
    icon: Smartphone,
    iconClassName: "bg-blue-500",
    description: "Versions, maintenance and links",
  },
  "activity-logs": {
    icon: History,
    iconClassName: "bg-violet-500",
    description: "Audit trail of system activity",
  },
};

const MOBILE_SECTION_GROUPS: Array<{ title: string; ids: string[] }> = [
  { title: "Company", ids: ["bank-details", "attachments"] },
  { title: "Customer app", ids: ["mobile-app"] },
  { title: "System", ids: ["activity-logs"] },
];

const SettingsRow: React.FC<{ tab: SettingsTab; onClick: () => void }> = ({ tab, onClick }) => {
  const meta = MOBILE_SECTION_META[tab.id];
  const Icon = meta.icon;

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 border-0 bg-white px-4 py-3 text-left transition-colors active:bg-gray-50 [-webkit-tap-highlight-color:transparent]"
    >
      <span
        className={cn(
          "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white",
          meta.iconClassName,
        )}
      >
        <Icon className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[15px] font-medium text-gray-900">{tab.label}</span>
        <span className="block truncate text-xs text-gray-500">{meta.description}</span>
      </span>
      <ChevronRight className="h-4 w-4 shrink-0 text-gray-300" />
    </button>
  );
};

const MobileSettings: React.FC<{
  tabs: SettingsTab[];
  onSelectLog: (log: ActivityLog | null) => void;
}> = ({ tabs, onSelectLog }) => {
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const activeTab = tabs.find((tab) => tab.id === searchParams.get("section"));

  const openSection = (id: string) =>
    navigate({ search: `?section=${id}` }, { state: { fromSettingsList: true } });

  const backTo = (location.state as { fromSettingsList?: boolean } | null)?.fromSettingsList
    ? -1
    : location.pathname;

  if (activeTab?.id === "activity-logs") {
    return <ActivityLogsSection onSelectLog={onSelectLog} mobileBackTo={backTo} />;
  }

  if (activeTab) {
    return (
      <PageShell key={activeTab.id} title={activeTab.label} mobile={{ backTo }}>
        {activeTab.content}
      </PageShell>
    );
  }

  const tabsById = new Map(tabs.map((tab) => [tab.id, tab]));

  return (
    <PageShell
      title="Settings"
      description="Manage your account, bank details, mobile app settings, attachments, and system activity."
    >
      <button
        type="button"
        onClick={() => openSection("account")}
        className="flex w-full items-center gap-3 rounded-2xl border border-gray-100 bg-white p-4 text-left transition-colors active:bg-gray-50 [-webkit-tap-highlight-color:transparent]"
      >
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-black text-lg font-medium text-white">
          {user ? getInitials(user.fullName) : <UserRound className="h-5 w-5" />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-base font-semibold text-gray-900">
            {user?.fullName ?? "Account"}
          </span>
          <span className="block truncate text-[13px] text-gray-500">
            {user?.email ?? MOBILE_SECTION_META.account.description}
          </span>
        </span>
        <ChevronRight className="h-4 w-4 shrink-0 text-gray-300" />
      </button>

      {MOBILE_SECTION_GROUPS.map((group) => (
        <div key={group.title}>
          <p className="px-4 pb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">
            {group.title}
          </p>
          <div className="divide-y divide-gray-100 overflow-hidden rounded-2xl border border-gray-100 bg-white">
            {group.ids.map((id) => {
              const tab = tabsById.get(id);
              return tab ? (
                <SettingsRow key={id} tab={tab} onClick={() => openSection(id)} />
              ) : null;
            })}
          </div>
        </div>
      ))}
    </PageShell>
  );
};

const Settings: React.FC = () => {
  const isMobile = useIsMobile();
  const [selectedLog, setSelectedLog] = useState<ActivityLog | null>(null);

  const tabs: SettingsTab[] = [
    {
      id: "account",
      label: "Account",
      content: <AccountSection />,
    },
    {
      id: "bank-details",
      label: "Bank details",
      content: <BankDetailsSection />,
    },
    {
      id: "mobile-app",
      label: "Mobile app",
      content: <MobileAppSettingsSection />,
    },
    {
      id: "attachments",
      label: "Attachments",
      content: <AttachmentTypesSection />,
    },
    {
      id: "activity-logs",
      label: "Activity logs",
      content: <ActivityLogsSection onSelectLog={setSelectedLog} />,
    },
  ];

  return (
    <>
      <ActivityLogDetailsDrawer
        log={selectedLog}
        onClose={() => setSelectedLog(null)}
      />
      {isMobile ? (
        <MobileSettings tabs={tabs} onSelectLog={setSelectedLog} />
      ) : (
        <PageShell
          title="Settings"
          description="Manage your account, bank details, mobile app settings, attachments, and system activity."
        >
          <CustomTabs tabs={tabs} defaultTab="activity-logs" />
        </PageShell>
      )}
    </>
  );
};

export default Settings;
